import { Mail, Package, PackageOpen, type LucideIcon } from 'lucide-react'
import { sdk } from '@/lib/sdk'
import { uploadMedia } from '@/components/features/media/mediaUpload'
import type { Parcel, ParcelCompany, ParcelKind, ParcelStatus, ParcelsModuleConfig } from '@/types/parcels'
import { retentionError, retentionInput } from '@/components/features/spaces/retentionUtils'

export const MAX_PHOTOS = 5
export const MAX_KIND_LABEL = 60
export const MAX_NOTES = 200
export const MAX_REASON = 200
export const MAX_LOCKER_LABEL = 60
export const MIN_LOCKER_CODE = 4
export const MAX_LOCKER_CODE = 12

export const PARCEL_KINDS: Array<{ value: ParcelKind; icon: LucideIcon }> = [
  { value: 'PACKAGE', icon: Package },
  { value: 'LETTER', icon: Mail },
  { value: 'OTHER', icon: PackageOpen }
]

export const kindIcon = (kind: ParcelKind): LucideIcon => PARCEL_KINDS.find((k) => k.value === kind)?.icon ?? Package

export const STATUS_VARIANT: Record<ParcelStatus, 'warning' | 'success' | 'info' | 'default'> = {
  RECEIVED: 'warning',
  PICKED_UP: 'success',
  RETURNED: 'info',
  DISCARDED: 'default',
  FORWARDED: 'success'
}

interface ApiErrorShape {
  message?: string
  response?: { status?: number; data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } }
}

export const apiStatus = (err: unknown): number | undefined => (err as ApiErrorShape | null)?.response?.status

export const apiMessage = (err: unknown, fallback: string): string => {
  const e = err as ApiErrorShape | null
  return e?.response?.data?.message || e?.message || fallback
}

/** Per-field messages out of an API refusal (`errors[].param`). */
export const apiFieldErrors = (err: unknown): Record<string, string> => {
  const errors = (err as ApiErrorShape | null)?.response?.data?.errors
  const list = Array.isArray(errors) ? errors : []
  const byField: Record<string, string> = {}
  for (const e of list) if (e.param && e.message && !byField[e.param]) byField[e.param] = e.message
  return byField
}

/* The queue of requests (F-245) carries only the company's id and name. */
type RecipientOf = Pick<Parcel, 'recipient'> & Partial<Pick<Parcel, 'recipientType'>> & {
  recipientCompany?: (Pick<ParcelCompany, 'name'> & Partial<ParcelCompany>) | null
}

export const isCompanyParcel = (parcel: RecipientOf): boolean =>
  parcel.recipientType === 'company' || (!parcel.recipient && Boolean(parcel.recipientCompany))

/** The recipient's name whichever shape the profile arrives in; for a company parcel, the company's name. */
export const recipientName = (parcel: RecipientOf): string =>
  isCompanyParcel(parcel)
    ? parcel.recipientCompany?.name || ''
    : parcel.recipient?.name || parcel.recipient?.displayName || ''

export const recipientAvatar = (parcel: RecipientOf): string | null =>
  isCompanyParcel(parcel) ? null : parcel.recipient?.avatar || parcel.recipient?.avatarUrl || null

/** The company the parcel is addressed to was closed or deleted: the parcel waits, nobody is told. */
export const companyGone = (parcel: RecipientOf): boolean =>
  isCompanyParcel(parcel) && (!parcel.recipientCompany || parcel.recipientCompany.removed || parcel.recipientCompany.status !== 'active')

/** Whole days between two instants, for "arrived 2 days ago". */
export const daysSince = (iso: string, now = Date.now()): number =>
  Math.max(0, Math.floor((now - new Date(iso).getTime()) / 86_400_000))

export const initialsOf = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')

export const DEFAULT_REMINDER_DAYS = 3
export const DEFAULT_REMINDER_COUNT = 2

export const defaultParcelsConfig = (): ParcelsModuleConfig => ({
  lockerMode: 'none',
  lockerPickupCode: false,
  emailIncludesPhoto: true,
  requireStaffConfirmation: false,
  reminderAfterDays: DEFAULT_REMINDER_DAYS,
  reminderMaxCount: DEFAULT_REMINDER_COUNT
})

/** The defaults under what the space stores (`null` days is a choice: reminders off). */
export const mergeParcelsConfig = (stored?: Partial<ParcelsModuleConfig> | null): ParcelsModuleConfig => {
  const base = defaultParcelsConfig()
  const merged = { ...base, ...(stored ?? {}) }
  return { ...merged, reminderAfterDays: stored && 'reminderAfterDays' in stored ? stored.reminderAfterDays ?? null : base.reminderAfterDays }
}

const inRange = (value: unknown, min: number, max: number) => Number.isInteger(value) && (value as number) >= min && (value as number) <= max

/** The slice that goes to the API: no locker code switch without a locker, no reminder count when reminders are off. */
export const toParcelsInput = (config: ParcelsModuleConfig): ParcelsModuleConfig => {
  const lockerMode = config.lockerMode === 'free' || config.lockerMode === 'list' ? config.lockerMode : 'none'
  return {
    lockerMode,
    lockerPickupCode: lockerMode !== 'none' && config.lockerPickupCode === true,
    emailIncludesPhoto: config.emailIncludesPhoto !== false,
    requireStaffConfirmation: config.requireStaffConfirmation === true,
    reminderAfterDays: config.reminderAfterDays ?? null,
    reminderMaxCount: inRange(config.reminderMaxCount, 1, 5) ? config.reminderMaxCount : DEFAULT_REMINDER_COUNT,
    ...retentionInput(config.retentionMonths)
  }
}

export interface ParcelsConfigErrors { reminderAfterDays?: string; reminderMaxCount?: string; retentionMonths?: string }

/** Same bounds as the API (days 1 to 60 or empty, count 1 to 5, retention 6 to 60); the server stays the judge. */
export const validateParcelsConfig = (config: ParcelsModuleConfig, t: (key: string) => string): ParcelsConfigErrors | null => {
  const errors: ParcelsConfigErrors = {}
  if (config.reminderAfterDays !== null && config.reminderAfterDays !== undefined && !inRange(config.reminderAfterDays, 1, 60)) errors.reminderAfterDays = t('parcels:config.reminder.afterDays.invalid')
  if (config.reminderAfterDays != null && !inRange(config.reminderMaxCount, 1, 5)) errors.reminderMaxCount = t('parcels:config.reminder.maxCount.invalid')
  const retention = retentionError(config.retentionMonths, t)
  if (retention) errors.retentionMonths = retention
  return Object.keys(errors).length ? errors : null
}

/** The space's path (`/{section}/{space}`) for a space id, found in the menu; `null` when the person does not see it. */
export const spacePathOf = (sections: Array<{ slug: string; spaces?: Array<{ id: string; slug: string }> }>, spaceId: string): string | null => {
  for (const section of sections) {
    const space = section.spaces?.find((s) => s.id === spaceId)
    if (space) return `/${section.slug}/${space.slug}`
  }
  return null
}

/** The space's title for a space id, found in the menu; `null` when the person does not see it there. */
export const spaceTitleOf = (sections: Array<{ spaces?: Array<{ id: string; title: string }> }>, spaceId: string): string | null => {
  for (const section of sections) {
    const space = section.spaces?.find((s) => s.id === spaceId)
    if (space) return space.title
  }
  return null
}

/** Uploads one photo through the hosted-media pipeline and resolves with the confirmed media id. */
export const uploadParcelPhoto = async (spaceId: string, file: File, opts: { onProgress?: (p: number) => void; signal?: AbortSignal } = {}): Promise<string> => {
  const { asset } = await uploadMedia({ purpose: 'parcelPhoto', spaceId, file, ...opts })
  return asset.id
}

/** An upload that never got attached: the file and the asset go away. Failures are not worth a message. */
export const discardUnattached = (mediaId: string) => { void sdk.media.delete(mediaId).catch(() => undefined) }
